import type { startseite } from "@progressive-development/politik-model";

import { anteil } from "./wahl-vergleich.js";

/**
 * Was aus einem Wahlergebnis für die Anzeige gebildet wird — reine Funktionen,
 * neben den Bausteinen, nicht in ihnen. Sie sind das, was getestet wird.
 *
 * WAS HIER GERECHNET WIRD, und was nicht (Fachkonzept 6, 12.3):
 *
 * - Anteile aus Stimmen und gültigen Stimmen: Arithmetik, keine Aussage.
 * - Die Kurzfassung „drei stärkste + weitere": eine Summe. Bis die Pipeline
 *   drei liefert (Lieferung 1), bildet die App sie aus den sechs gelieferten.
 * - Die Differenz in Prozentpunkten zwischen zwei Wahlen: eine Subtraktion,
 *   deren Vorzeichen ein Zeichen bleibt.
 *
 * NICHT hier: welche Wahl die aktuelle ist (`wahl-vergleich.ts → teilwahl()`),
 * Sitze (kommen aus den Daten), eine Zuordnung über Namen (nie — nur über
 * den Partei-Slug).
 */

/** Eine Zeile der Ergebnisliste — das, was `app-wahl-ergebnisliste` zeigt. */
export interface Ergebniszeile {
  /** Kuratierter Slug; ohne ihn keine Farbe und kein Vergleichswert. */
  partei?: string;
  /** Beschriftung: Kurzname, sonst Bezeichnung, sonst Slug — nie eine Umschrift. */
  name: string;
  stimmen: number;
  /** Anteil an den gültigen Stimmen, in Prozent. Fehlt ohne Bezugsgröße. */
  anteil?: number;
  /**
   * Derselbe Anteil in der Gesamtstadt — der Klammerwert der Gebietsseite.
   * Fehlt stadtweit oder bei fehlender Bezugsgröße; nie 0.
   */
  klammerwert?: number;
}

/** Die Sammelzeile „weitere" — benannt, damit niemand verschwindet. */
export interface WeitereZeile {
  anzahl: number;
  stimmen: number;
  anteil?: number;
}

/** Wie viele Kräfte die Kurzfassung nennt — überall dieselbe Zahl (6.8). */
export const KURZFASSUNG_KRAEFTE = 3;

/** Beschriftung einer Kraft ohne Umschrift: Kurzname vor Bezeichnung vor Slug. */
function name(k: { kurzname?: string; bezeichnung?: string; partei?: string }): string {
  return k.kurzname ?? k.bezeichnung ?? k.partei ?? "";
}

/**
 * Die Zeilen eines Ergebnisses — alle Kräfte, nach Stimmen absteigend.
 *
 * Bei Gleichstand bleibt die Reihenfolge der Quelle (stabile Sortierung): Ein
 * zweites Kriterium zu erfinden hieße, eine Rangfolge zu behaupten.
 *
 * Mit `stadt` bekommt jede Zeile ihren Klammerwert — den Anteil DERSELBEN
 * Partei in der Gesamtstadt, zugeordnet über den Slug. Eine Partei, die
 * stadtweit nicht unter den genannten ist, bekommt keinen; das ist eine
 * Lücke, keine Null.
 */
export function ergebniszeilen(
  ergebnis: startseite.Wahlzusammenfassung | undefined,
  stadt?: startseite.Wahlzusammenfassung,
): Ergebniszeile[] {
  const kraefte = ergebnis?.staerkste ?? [];
  const stadtStimmen = new Map(
    (stadt?.staerkste ?? [])
      .filter((k) => k.partei)
      .map((k) => [k.partei as string, k.stimmen] as const),
  );
  return [...kraefte]
    .sort((a, b) => b.stimmen - a.stimmen)
    .map((k) => ({
      partei: k.partei,
      name: name(k),
      stimmen: k.stimmen,
      anteil: anteil(k.stimmen, ergebnis?.gueltigeStimmen),
      klammerwert:
        k.partei && stadtStimmen.has(k.partei)
          ? anteil(stadtStimmen.get(k.partei), stadt?.gueltigeStimmen)
          : undefined,
    }));
}

/**
 * Die Kurzfassung: die drei stärksten Kräfte und „weitere" als Sammelzeile.
 *
 * Liefert die Zusammenfassung mehr als drei Kräfte (heute sechs), wandern die
 * übrigen in die Sammelzeile — Anzahl und Stimmen werden zu denen addiert,
 * die die Pipeline schon als `weitere` führt. Das ist eine Summe, keine
 * Auswahl: Die Auswahl (nach Stimmen) hat die Pipeline getroffen.
 *
 * Die Sammelzeile fehlt, wo es nichts zu sammeln gibt (Stichwahl mit zwei
 * Bewerbungen).
 */
export function kurzfassung(
  ergebnis: startseite.Wahlzusammenfassung | undefined,
  stadt?: startseite.Wahlzusammenfassung,
  anzahl = KURZFASSUNG_KRAEFTE,
): { zeilen: Ergebniszeile[]; weitere?: WeitereZeile } {
  const alle = ergebniszeilen(ergebnis, stadt);
  const zeilen = alle.slice(0, anzahl);
  const rest = alle.slice(anzahl);
  const geliefert = ergebnis?.weitere;
  const restAnzahl = rest.length + (geliefert?.anzahl ?? 0);
  if (restAnzahl === 0) return { zeilen };
  const restStimmen = rest.reduce((s, z) => s + z.stimmen, 0) + (geliefert?.stimmen ?? 0);
  return {
    zeilen,
    weitere: {
      anzahl: restAnzahl,
      stimmen: restStimmen,
      anteil: anteil(restStimmen, ergebnis?.gueltigeStimmen),
    },
  };
}

// -----------------------------------------------------------------------------
// Sitze
// -----------------------------------------------------------------------------

/**
 * Eine Zeile der Sitzzeile — die gemeinsame Form von `wahl.SitzAnteil` (BV,
 * gerechnet) und `wahl.ErgebnisSichtSitz` (Rat amtlich: ohne Stimmen).
 */
export interface SitzZeile {
  partei?: string;
  kurzname?: string;
  bezeichnung?: string;
  stimmen?: number;
  anteil?: number;
  sitze?: number;
}

/**
 * Sitze nach Sitzen absteigend, bei Gleichstand in der Reihenfolge der
 * Quelle — dieselbe Regel wie bei den Stimmen.
 */
export function sortiereSitzAnteile<T extends SitzZeile>(eintraege: readonly T[]): T[] {
  return [...eintraege].sort((a, b) => (b.sitze ?? 0) - (a.sitze ?? 0));
}

// -----------------------------------------------------------------------------
// Vergleich über die Zeit (Detailseite, Teil D)
// -----------------------------------------------------------------------------

/** Eine Kraft in einem der beiden Ergebnisse — die Form der Ergebnis-Sicht. */
export interface VergleichsKraft {
  partei?: string;
  kurzname?: string;
  bezeichnung?: string;
  stimmen: number;
  /** Liegt er vor, wird er genommen; sonst aus Stimmen und Bezugsgröße gebildet. */
  anteil?: number;
}

/** Ein Ergebnis, soweit der Vergleich es braucht. */
export interface VergleichsErgebnis {
  stimmen: VergleichsKraft[];
  gueltigeStimmen?: number;
  wahlbeteiligung?: number;
}

/** Eine Zeile des Zeitvergleichs: vorher · jetzt · Differenz. */
export interface ZeitVergleichszeile {
  partei?: string;
  name: string;
  vorher?: number;
  jetzt?: number;
  /** Prozentpunkte; nur, wenn beide Anteile vorliegen. */
  differenz?: number;
}

export interface ZeitVergleich {
  /** In der Reihenfolge der VORIGEN Wahl, nach deren Stimmen absteigend. */
  zeilen: ZeitVergleichszeile[];
  beteiligung?: { vorher?: number; jetzt?: number; differenz?: number };
  /** Nur bei der vorigen Wahl angetreten — genannt, nicht mit 0 % geführt. */
  nurVorher: string[];
  /** Nur bei der aktuellen Wahl angetreten. */
  nurJetzt: string[];
}

function anteilVon(k: VergleichsKraft, gueltig: number | undefined): number | undefined {
  return k.anteil ?? anteil(k.stimmen, gueltig);
}

function differenz(vorher: number | undefined, jetzt: number | undefined): number | undefined {
  return vorher !== undefined && jetzt !== undefined ? jetzt - vorher : undefined;
}

/**
 * Stellt dieselbe Teilwahl am selben Ort zweier Wahlen gegenüber.
 *
 * Die Zeilen folgen der VORIGEN Wahl, nach deren eigenen Stimmen — so liest
 * sich der Block als eigenes Ergebnis, nicht als Kommentar zum aktuellen
 * (Fachkonzept 2.3 D). Zugeordnet wird nur über den Partei-Slug; Kräfte ohne
 * Slug bleiben in ihrer Wahl und erscheinen als „nur vorher" bzw. „nur
 * jetzt", weil ohne Schlüssel nichts zusammenzuführen ist.
 */
export function zeitVergleich(
  jetzt: VergleichsErgebnis | undefined,
  vorher: VergleichsErgebnis | undefined,
): ZeitVergleich | undefined {
  if (!jetzt || !vorher) return undefined;

  const jetztJeSlug = new Map(
    jetzt.stimmen.filter((k) => k.partei).map((k) => [k.partei as string, k] as const),
  );
  const vorherSlugs = new Set(vorher.stimmen.map((k) => k.partei).filter(Boolean));

  const zeilen: ZeitVergleichszeile[] = [];
  const nurVorher: string[] = [];
  for (const k of [...vorher.stimmen].sort((a, b) => b.stimmen - a.stimmen)) {
    const gegenueber = k.partei ? jetztJeSlug.get(k.partei) : undefined;
    if (!gegenueber) {
      nurVorher.push(name(k));
      continue;
    }
    const v = anteilVon(k, vorher.gueltigeStimmen);
    const j = anteilVon(gegenueber, jetzt.gueltigeStimmen);
    zeilen.push({
      partei: k.partei,
      name: name(k),
      vorher: v,
      jetzt: j,
      differenz: differenz(v, j),
    });
  }
  const nurJetzt = jetzt.stimmen.filter((k) => !k.partei || !vorherSlugs.has(k.partei)).map(name);

  return {
    zeilen,
    beteiligung:
      vorher.wahlbeteiligung !== undefined || jetzt.wahlbeteiligung !== undefined
        ? {
            vorher: vorher.wahlbeteiligung,
            jetzt: jetzt.wahlbeteiligung,
            differenz: differenz(vorher.wahlbeteiligung, jetzt.wahlbeteiligung),
          }
        : undefined,
    nurVorher,
    nurJetzt,
  };
}
